import {PriceStrategyService} from "../../components/inputs/price/service/PriceStrategyService";
import {isOptionQuote, isTmcQuote, isWarrantQuote, QuoteRecord, QuoteRecords} from "../models/QuoteRecord";
import {Optional} from "../../js/util/Optional";
import {quoteHashMap} from "../util/QuoteStoreUtil";
import {
    OptionQuoteViewModel,
    QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel,
    WarrantQuoteViewModel
} from "../models/QuoteViewModel";
import {isNotNull} from "../../js/util/UtilFunctions";

export class QuoteModelFactory {

    public static readonly id = "quoteModelFactory";

    /** @ngInject */
    public constructor(private readonly priceStrategyService: PriceStrategyService) {
    }

    public buildViewModels(quotes: QuoteRecords, previousQuotes: QuoteRecords): ReadonlyArray<QuoteViewModel> {
        const previousMap = quoteHashMap<QuoteRecord>();
        previousQuotes.forEach((q) => previousMap.set(q, q));

        return quotes.map((q) => {
            const previous = previousMap.get(q);
            return this.tryBuildModel(q, previous);
        }).filter(isNotNull);
    }

    private tryBuildModel<T extends QuoteRecord>(quote: T, previousQuote: T | null = null): QuoteViewModel | null {
        try {
            return this.buildViewModel(quote, previousQuote);
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    public buildViewModel<T extends QuoteRecord>(quote: T,
                                                 previousQuote: T | null = null): QuoteViewModel {
        const strategy = this.priceStrategyService.forCode(quote.code);
        if (isTmcQuote(quote) && ((previousQuote == null) || (isTmcQuote(previousQuote)))) {
            const prevLegs = Optional.of(previousQuote)
                                     .map((pm) => pm.legs)
                                     .orElse([]);
            const legQuotes = this.buildViewModels(quote.legs, prevLegs);
            return new TmcQuoteViewModel(quote, previousQuote, legQuotes, strategy);
        } else if (isOptionQuote(quote) && ((previousQuote == null) || (isOptionQuote(previousQuote)))) {
            return new OptionQuoteViewModel(quote, previousQuote, strategy);
        } else if (isWarrantQuote(quote) && ((previousQuote == null) || (isWarrantQuote(previousQuote)))) {
            return new WarrantQuoteViewModel(quote, previousQuote, strategy);
        } else {
            return new UnderlyingQuoteViewModel(quote, previousQuote, strategy);
        }
    }
}
